<template>
  <v-autocomplete
    outlined
    v-model="selected"
    :items="items"
    :menu-props="{ maxHeight: '400', zIndex: 13 }"
    :label="label"
    :multiple="multiple"
    small-chips
    deletable-chips
    :item-text="itemText"
    item-value="id"
  ></v-autocomplete>
</template>

<script>
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";

import {
  computed,
  onMounted,
  reactive,
  set,
  watch,
} from "@vue/composition-api";

import useProxy from "@/hooks/useProxy";
import useQueryString from "@/hooks/useQueryString";

import { URL_PARAM_NAMES } from "@/utils/constants";

const COMPONENT_NAME = "RedacFilter";

/**
 * Either call this component giving an `initialValue` (int[])
 *  and listening on `valueChanged` event,
 * Or call it specifying a `store`
 *  (and eventually `storeGetter`/`storeUpdater`)
 */
export default {
  name: COMPONENT_NAME,
  props: {
    initialValue: {
      type: Array,
      validator(values) {
        return (
          values.length === 0 ||
          values.every((value) => typeof value === "number")
        );
      },
    },
    store: {
      type: String,
      validator(value) {
        /* List of stores having :
          - getters["<store>/<storeGetter>"]
          - dispatch("<store>/<storeUpdater>", payload)
          */
        return ["redac", "audience"].includes(value);
      },
    },
    storeGetter: {
      type: String,
      default: "getRedacs",
    },
    storeUpdater: {
      type: String,
      default: "setRedacs",
    },
    storeSiteGetter: {
      type: String,
      default: "getSites",
    },
    multiple: {
      type: Boolean,
      default: true,
    },
    dependsOnSites: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const { store } = useProxy();

    const { addQueryStringParam, synchronizeFilterWithQueryString } =
      useQueryString();

    const page = reactive({
      isInitialized: false,
      initialUrlValue: [], // in case we need to load from url params
    });

    // =>  METHODS
    const itemText = (item) =>
      `${item.name} (${item.email} - ${item.site_id} - ${item.site_name})`;

    const debouncedRefreshRedacList = debounce(async function () {
      // Need debounce in case siteFilter is set in URL: to be ran instead of the
      // default full redac list (starting before / ending after)
      await store.dispatch(
        "common/getRedacList",
        props.dependsOnSites
          ? {
              site_ids: sites.value.join(","),
            }
          : {},
      );
    }, 500);

    // =>  COMPUTED
    const items = computed(() => store.getters["common/getRedacList"]);

    const viaStore = computed(() => props.store !== undefined);
    const sites = computed(
      () => store.getters[`${props.store}/${props.storeSiteGetter}`],
    );

    const selected = computed({
      get() {
        if (viaStore.value) {
          return store.getters[`${props.store}/${props.storeGetter}`];
        }

        return page.initialUrlValue ?? props.initialValue;
      },
      set(e) {
        if (viaStore.value === false) {
          if (!isEqual(e, page.initialUrlValue ?? props.initialValue)) {
            emit("valueChanged", e);
          }

          return;
        }

        store.dispatch(`${props.store}/${props.storeUpdater}`, e);
      },
    });

    const label = computed(() =>
      props.multiple ? "Sélectionner les redacs" : "Sélectionner un redac",
    );

    // =>  WATCH
    watch(selected, (newValue) => {
      if (page.isInitialized) {
        // Only update user changes, not initialization
        addQueryStringParam({
          param: URL_PARAM_NAMES[COMPONENT_NAME],
          value: newValue,
          is_multiple: props.multiple,
          is_integer: true,
          // is_boolean: false,
        });
      }
    });

    watch(sites, async () => {
      await debouncedRefreshRedacList();
    });

    // =>  HOOKS
    onMounted(async () => {
      await debouncedRefreshRedacList();

      set(
        page,
        "initialUrlValue",
        await synchronizeFilterWithQueryString({
          dispatcher: viaStore.value
            ? `${props.store}/${props.storeUpdater}`
            : "",
          param: URL_PARAM_NAMES[COMPONENT_NAME],
          value: selected.value,
          is_multiple: props.multiple,
          is_integer: true,
          // is_boolean: false,
          dependsOn: props.dependsOnSites
            ? URL_PARAM_NAMES["SiteFilter"]
            : undefined,
        }),
      );

      page.isInitialized = true;
    });

    // =>  DATA
    return {
      items,
      itemText,
      label,
      selected,
    };
  },
};
</script>

<style scoped>
[role="menu"] {
  top: 233px !important;
  right: 0 !important;
}
</style>
